export enum Tag {
  ANIMAL = 'ANIMAL', // állatábrázolás
  HUMAN = 'HUMAN', // emberábrázolás
  GEOMETRIC = 'GEOMETRIC', // geometrikus minta
  BIRD = 'BIRD', // madár
  SHAPE = 'SHAPE', // alakzat
  CREST = 'CREST', // címer
  TEXT = 'TEXT', // felirat
  FLORAL = 'FLORAL', // növényi díszítés
  RELIGIOUS = 'RELIGIOUS', // vallási motívum
  PATTERN = 'PATTERN', // ismétlődő minta
  BLUEDYE = 'BLUEDYE', // kékfestő
}

// Runtime source of themes is VITE_BACKEND_URL + '/themes'.

const allTags = Object.values(Tag)

const createEmptyTagMap = (allTags: Tag[]): Record<Tag, number[]> => {
  return allTags.reduce((acc, tag) => {
    acc[tag] = []
    return acc
  }, {} as Record<Tag, number[]>)
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const toFiniteNumber = (value: unknown): number | undefined => {
  const num = Number(value)
  return Number.isFinite(num) ? num : undefined
}

const toPositiveId = (value: unknown): number | undefined => {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? num : undefined
}

const toTagMap = (payload: Record<string, unknown>, allTags: Tag[]): Record<Tag, number[]> => {
  return allTags.reduce((acc, tag) => {
    const value = payload[tag]
    acc[tag] = Array.isArray(value)
      ? value.map(toPositiveId).filter((num): num is number => num !== undefined)
      : []
    return acc
  }, {} as Record<Tag, number[]>)
}

export interface BackendKeywordTag {
  id: number
  Name: string
  EN_Name: string
  items: number[]
  count: number
  type: string
}

const toKeywordTags = (payload: unknown): BackendKeywordTag[] => {
  if (!Array.isArray(payload)) {
    throw new Error('Tag payload did not contain a valid keyword-tag array.')
  }

  return payload.filter(isRecord).map(entry => {
    const rawItems = Array.isArray(entry.items) ? entry.items : []
    const items = rawItems.map(toPositiveId).filter((num): num is number => num !== undefined)

    return {
      id: toPositiveId(entry.id) ?? 0,
      Name: typeof entry.Name === 'string' ? entry.Name : '',
      EN_Name: typeof entry.EN_Name === 'string' ? entry.EN_Name : '',
      items,
      count: toFiniteNumber(entry.count) ?? items.length,
      type: typeof entry.type === 'string' ? entry.type : '',
    }
  })
}

export let tags: Record<Tag, number[]> = createEmptyTagMap(allTags)
export let keywordTags: BackendKeywordTag[] = []

export const loadThemesFromBackend = async () => {
  const backendUrl = import.meta.env.VITE_BACKEND_URL
  if (!backendUrl) return

  const endpoint = '/themes'
  const url = endpoint.startsWith('http')
    ? endpoint
    : `${backendUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`

  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch tags. HTTP ${response.status}`)
    }

    const json = await response.json()
    const payload =
      isRecord(json) && isRecord(json.themes)
        ? json.themes
        : isRecord(json) && isRecord(json.tags)
        ? json.tags
        : json

    if (!isRecord(payload)) {
      console.warn('Tag payload did not contain a valid tag-id map. Themes map stays empty.')
      return
    }

    tags = toTagMap(payload, allTags)
  } catch (error) {
    console.error('Could not load tags from backend. Themes map stays empty.', error)
  }
}

export const loadTagsFromBackend = async () => {
  const backendUrl = import.meta.env.VITE_BACKEND_URL
  if (!backendUrl) return

  const endpoint = '/tags'
  const url = endpoint.startsWith('http')
    ? endpoint
    : `${backendUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`

  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch tags. HTTP ${response.status}`)
    }

    const json = await response.json()
    const payload = isRecord(json) && Array.isArray(json.tags) ? json.tags : json
    const parsedTags = toKeywordTags(payload)

    if (!parsedTags.length) {
      console.warn('Tag payload did not contain a valid keyword-tag array.')
      return
    }

    keywordTags = parsedTags
  } catch (error) {
    console.error('Could not load keyword tags from backend. Keyword list stays empty.', error)
  }
}

export const tagIcons: Partial<Record<Tag, string>> = {
  [Tag.HUMAN]: '/icons/filter-buttons/human.svg',
  [Tag.GEOMETRIC]: '/icons/filter-buttons/geometric.svg',
  [Tag.TEXT]: '/icons/filter-buttons/text.svg',
  [Tag.CREST]: '/icons/filter-buttons/crest.svg',
  [Tag.FLORAL]: '/icons/filter-buttons/floral.svg',
  [Tag.BIRD]: '/icons/filter-buttons/bird.svg',
  [Tag.ANIMAL]: '/icons/filter-buttons/animal.svg',
  [Tag.SHAPE]: '/icons/filter-buttons/shape.svg',
  [Tag.RELIGIOUS]: '/icons/filter-buttons/religious.svg',
  [Tag.PATTERN]: '/icons/filter-buttons/pattern.svg',
}
